import { useState } from "react";

import { ApiError } from "@/api/client";
import { useProjectsQuery } from "@/api/hooks";
import {
  currentAdoptionsVersionFromError,
  useAssetAdoptionsQuery,
  useLibraryAssetsQuery,
  useLibraryPlacementRegistryQuery,
  useProjectAdoptionsQuery,
  useReplaceProjectAdoptionsMutation,
  type LibraryAsset,
} from "@/api/library";
import {
  adoptionImpact,
  adoptionPolicyReady,
  POLICY_UNREAD_SENTENCE,
  singlePinSentence,
} from "@/lib/adoptions";
import { useCapability } from "@/auth/SessionContext";
import { formatDate } from "@/lib/format";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Notice } from "@/ui/Notice";
import { Tooltip } from "@/ui/Tooltip";
import { EmptyState, ErrorState, LoadingState } from "@/ui/states";

/**
 * Pinning ONE alert matrix into a project (#35).
 *
 * WHY IT IS HERE AND NOT ON THE PROJECT. A project's clinical-configuration
 * surfaces for safety and alerts are gated on #29 G3, which has not started:
 * `GET /v1/projects/{projectId}/sections/{section}` still answers 501 and is the
 * only producer of `SectionConfig`. The library asset page needs none of that —
 * the adoption endpoints have been server-side complete since #28 — so the pin
 * is offered where the matrix already is. The generic pin-and-diff surface for
 * every clinical configuration stays with #36.
 *
 * CARDINALITY COMES FROM THE SERVED REGISTRY. The server currently bounds this
 * family at one adopted pin and keeps a partial unique index beneath that
 * preflight, but neither fact is compiled into this surface. The browser reads
 * `families[].cardinality.adopted.max`, presents the served rule and derives a
 * displacement only when that rule says `max: 1`. If the registry is unread, a
 * pin/add/move fails closed while an unpin remains the recovery path.
 *
 * WHOLE-SET REPLACEMENT. `PUT /adoptions` replaces every pin the project holds,
 * so the dialog reads the current set first and sends it back with exactly one
 * alert-matrix entry changed. A surface that sent only its own pin would unpin
 * every questionnaire and pathway the project had adopted.
 */

/**
 * The service's own archived-adoption rule, presented rather than re-derived —
 * the same relationship this surface has to the one-matrix cap.
 *
 * `PUT /adoptions` keeps an archived asset's existing pins and allows them to
 * be removed, and refuses one added or moved with a 409. Removal staying legal
 * is why `Unpin` is untouched below.
 */
const ARCHIVED_SENTENCE =
  "This matrix is archived. An archived asset's existing pins may be kept or removed, but not added or moved.";

/**
 * Why this matrix cannot be pinned into a project or moved to a newer version,
 * or `null` when it can.
 *
 * ONE derivation for both write actions, shaped like `lifecycleRefusal`: the
 * missing capability comes first, because a caller without `draft.write` is
 * refused whatever the asset's state would have allowed. An archived matrix
 * then refuses the same writes the API refuses, which is ADR 0001's
 * no-dead-controls rule — a Pin whose only possible outcome is a 409 is not an
 * offer, and the lifecycle promise is that an archived asset is not put
 * forward for new adoption.
 */
function adoptionRefusal(
  asset: LibraryAsset,
  missingWriteReason: string | null,
  policyUnread: boolean,
): string | null {
  if (missingWriteReason) {
    return missingWriteReason;
  }
  if (asset.capabilities.archived) {
    return ARCHIVED_SENTENCE;
  }
  return policyUnread ? POLICY_UNREAD_SENTENCE : null;
}

function assetName(
  assetId: string,
  catalog: readonly LibraryAsset[],
): string | null {
  return catalog.find((asset) => asset.id === assetId)?.name ?? null;
}

export function MatrixPinning({ asset }: { asset: LibraryAsset }) {
  const projects = useProjectsQuery("");
  const adoptions = useAssetAdoptionsQuery(asset.id);
  const placementRegistry = useLibraryPlacementRegistryQuery();
  const pinWrite = useCapability("draft.write");
  const [pending, setPending] = useState<{
    projectId: string;
    projectName: string;
    mode: "pin" | "unpin";
  } | null>(null);

  if (projects.isPending || adoptions.isPending) {
    return <LoadingState label="Loading project pins" rows={3} />;
  }
  if (projects.isError || adoptions.isError) {
    return (
      <ErrorState
        title="Project pins could not be loaded"
        description="The Editor Service did not return the projects, or which of them pin this matrix."
        onRetry={() => {
          void projects.refetch();
          void adoptions.refetch();
        }}
      />
    );
  }
  if (projects.data.items.length === 0) {
    return (
      <EmptyState
        title="No project to pin this matrix into"
        description="Create a project first; a matrix is adopted by pinning it to one."
      />
    );
  }

  const pinnedVersionBy = new Map(
    adoptions.data.items.map((row) => [row.projectId, row]),
  );
  // Both write actions share one verdict; `Unpin` deliberately does not,
  // because removing an archived matrix's pin is the one adoption write the
  // service still accepts.
  const writeRefusal = adoptionRefusal(
    asset,
    pinWrite.reason,
    !placementRegistry.isSuccess,
  );
  const capSentence = singlePinSentence(placementRegistry.data);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {capSentence ?? POLICY_UNREAD_SENTENCE}
      </p>
      {asset.capabilities.archived ? (
        <p className="text-sm text-muted-foreground">{ARCHIVED_SENTENCE}</p>
      ) : null}
      <ul className="divide-y divide-border rounded-md border border-border">
        {projects.data.items.map((project) => {
          const held = pinnedVersionBy.get(project.id);
          const label = held
            ? `Unpin from ${project.name}`
            : `Pin to ${project.name}`;
          // A project pinned to an older version needs a way FORWARD. Without
          // one the only route from v2 to v3 is unpin-then-repin, which is two
          // writes with a window in between where the project has no alert
          // matrix at all — and the ticket asks for update impact, not just
          // adoption impact. `pin` mode already replaces whatever alert-matrix
          // pin the project holds, including this asset's own, so the update
          // is that same confirmation reached from a second control.
          const behind = held !== undefined && held.version !== asset.latestVersion;
          return (
            <li
              key={project.id}
              className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 text-sm"
            >
              <span className="min-w-0 flex-1 font-medium text-foreground">
                {project.name}
              </span>
              {held ? (
                <>
                  <Badge tone="accent">Pinned {held.version}</Badge>
                  <span className="text-muted-foreground">
                    Pinned {formatDate(held.pinnedAt)}
                  </span>
                </>
              ) : (
                <span className="text-muted-foreground">
                  Does not pin this matrix
                </span>
              )}
              {behind ? (
                <Tooltip
                  content={
                    writeRefusal ??
                    `Move ${project.name}'s pin from ${held.version} to ${asset.latestVersion} in one replacement`
                  }
                >
                  <Button
                    size="sm"
                    aria-label={`Update ${project.name} to ${asset.latestVersion}`}
                    disabled={writeRefusal !== null}
                    onClick={() =>
                      setPending({
                        projectId: project.id,
                        projectName: project.name,
                        mode: "pin",
                      })
                    }
                  >
                    {`Update to ${asset.latestVersion}`}
                  </Button>
                </Tooltip>
              ) : null}
              <Tooltip
                content={
                  held
                    ? (pinWrite.reason ??
                      `Remove ${project.name}'s pin on this matrix`)
                    : (writeRefusal ??
                      `Pin ${asset.latestVersion} into ${project.name}`)
                }
              >
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={label}
                  disabled={held ? !pinWrite.allowed : writeRefusal !== null}
                  onClick={() =>
                    setPending({
                      projectId: project.id,
                      projectName: project.name,
                      mode: held ? "unpin" : "pin",
                    })
                  }
                >
                  {held ? "Unpin" : "Pin"}
                </Button>
              </Tooltip>
            </li>
          );
        })}
      </ul>

      {pending ? (
        <MatrixPinDialog
          asset={asset}
          projectId={pending.projectId}
          projectName={pending.projectName}
          mode={pending.mode}
          onClose={() => setPending(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * The confirmation, with the adoption impact shown before the write.
 *
 * The confirm control stays disabled until the project's current pin set AND
 * the catalog that names those pins have both loaded — the same fail-closed
 * rule `LifecycleDialog` applies, for the same reason: a replacement decided
 * without seeing what it replaces is not a decision. The catalog is not
 * optional here; without it the surface cannot say WHICH matrix a confirmation
 * would displace, only that one exists.
 */
function MatrixPinDialog({
  asset,
  projectId,
  projectName,
  mode,
  onClose,
}: {
  asset: LibraryAsset;
  projectId: string;
  projectName: string;
  mode: "pin" | "unpin";
  onClose: () => void;
}) {
  const current = useProjectAdoptionsQuery(projectId);
  const catalog = useLibraryAssetsQuery(true);
  const placementRegistry = useLibraryPlacementRegistryQuery();
  const replace = useReplaceProjectAdoptionsMutation(projectId);
  const busy = replace.isPending;
  const readsCurrent = current.isSuccess && catalog.isSuccess;
  const policyReady = adoptionPolicyReady(placementRegistry, mode);
  const ready = readsCurrent && policyReady;
  const impact = ready
    ? adoptionImpact(
        mode === "pin"
          ? { mode, assetId: asset.id, version: asset.latestVersion }
          : { mode, assetId: asset.id },
        current.data.items,
        catalog.data.items,
        placementRegistry.data,
      )
    : null;
  const capSentence = singlePinSentence(placementRegistry.data);

  /**
   * The alert-matrix pin this project currently holds, for the stale-unpin
   * explanation only. A PIN displacement comes from `adoptionImpact` and the
   * served registry below; this lookup never decides cardinality.
   */
  const currentlyHeldMatrix = readsCurrent
    ? (current.data.items.find((pin) =>
        catalog.data.items.some(
          (candidate) =>
            candidate.id === pin.assetId &&
            candidate.assetType === "alert_matrix",
        ),
      ) ?? null)
    : null;
  /**
   * This project's pin on THIS asset, which is the only pin an unpin may
   * remove.
   *
   * The two reads disagree by design. The list decided `unpin` from the
   * asset-centric adoption read; this dialog reloads the project's whole set
   * afterwards. Between them another clinician can move the project onto a
   * DIFFERENT matrix, at which point `held` is theirs — and removing `held`
   * would delete a pin nobody here asked about, against a freshly loaded
   * `adoptionsVersion` the server would accept.
   */
  const heldOnThisAsset = readsCurrent
    ? (current.data.items.find((pin) => pin.assetId === asset.id) ?? null)
    : null;
  /** Unpinning something that is no longer pinned: refuse rather than guess. */
  const pinMoved = mode === "unpin" && readsCurrent && heldOnThisAsset === null;
  const held = currentlyHeldMatrix;
  const displaced = impact?.displaced ?? null;
  const otherPins = impact?.unchanged ?? [];

  async function confirm() {
    if (!current.isSuccess || !catalog.isSuccess || !policyReady || impact === null) {
      return;
    }
    if (mode === "unpin" && heldOnThisAsset === null) {
      // Belt and braces with the disabled control: the set is re-checked at
      // the moment of the write, not only at the moment it was rendered.
      return;
    }
    try {
      await replace.mutateAsync({
        expectedAdoptionsVersion: current.data.adoptionsVersion,
        // `adoptionImpact` derives both the statement above and this whole-set
        // request from the served cardinality document, so neither can drift.
        items: [...impact.items],
      });
      onClose();
    } catch {
      // Rendered below from `replace.error`; the dialog stays open so the
      // refusal is read beside the impact that motivated the attempt.
    }
  }

  const staleVersion = replace.isError
    ? currentAdoptionsVersionFromError(replace.error)
    : undefined;
  const failure = replace.isError
    ? staleVersion
      ? `Another change to ${projectName}'s pins landed first (now ${staleVersion}). Nothing was written. Close this and try again to decide against the current set.`
      : replace.error instanceof ApiError
        ? replace.error.message
        : "The Editor Service could not be reached, so nothing was pinned."
    : null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        busy={busy}
        title={
          mode === "pin"
            ? `Pin ${asset.latestVersion} into ${projectName}`
            : `Remove ${projectName}'s pin`
        }
        description={
          mode === "pin"
            ? `${projectName} will use this matrix at ${asset.latestVersion} until the pin is moved.`
            : `${projectName} will pin no alert matrix.`
        }
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!ready || busy || pinMoved}
              onClick={() => void confirm()}
            >
              {mode === "pin" ? "Pin this matrix" : "Remove pin"}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm">
          {current.isPending || catalog.isPending ? (
            <LoadingState label="Loading this project's pins" rows={2} />
          ) : current.isError || catalog.isError ? (
            <ErrorState
              title="This project's pins could not be loaded"
              description="Until they load, the change is not offered — replacing a pin without seeing the set it belongs to is not a decision."
              onRetry={() => {
                void current.refetch();
                void catalog.refetch();
              }}
            />
          ) : mode === "pin" && placementRegistry.isPending ? (
            <LoadingState label="Loading the placement policy" rows={2} />
          ) : mode === "pin" && placementRegistry.isError ? (
            <ErrorState
              title="The placement policy could not be loaded"
              description={POLICY_UNREAD_SENTENCE}
              onRetry={() => void placementRegistry.refetch()}
            />
          ) : (
            <>
              {mode === "pin" && capSentence ? (
                <p className="text-muted-foreground">{capSentence}</p>
              ) : null}
              {mode === "pin" ? (
                held === null ? (
                  <p className="text-foreground">
                    {projectName} pins no alert matrix today. This adds one.
                  </p>
                ) : held.assetId === asset.id ? (
                  <p className="text-foreground">
                    This moves {projectName}&apos;s pin on this matrix from{" "}
                    {held.version} to {asset.latestVersion}.
                  </p>
                ) : displaced?.assetId === held.assetId ? (
                  <p className="text-foreground">
                    {projectName} currently pins{" "}
                    <strong>
                      {assetName(held.assetId, catalog.data.items) ??
                        "another alert matrix"}
                    </strong>{" "}
                    at {held.version}. Confirming replaces that pin — it is not
                    added alongside it.
                  </p>
                ) : (
                  <p className="text-foreground">
                    {projectName} currently pins{" "}
                    <strong>
                      {assetName(held.assetId, catalog.data.items) ??
                        "another alert matrix"}
                    </strong>{" "}
                    at {held.version}. The served placement policy does not name
                    that pin as displaced, so confirming adds this matrix
                    alongside it.
                  </p>
                )
              ) : pinMoved ? (
                <Notice role="alert" tone="destructive">
                  {projectName} no longer pins this matrix — the pin changed
                  after this list was read
                  {held
                    ? `, and the project now pins ${
                        assetName(held.assetId, catalog.data.items) ??
                        "another alert matrix"
                      } at ${held.version}`
                    : ""}
                  . Nothing was removed, and this dialog will not remove someone
                  else&apos;s pin. Close it and reopen the list to decide against
                  the current set.
                </Notice>
              ) : (
                <p className="text-foreground">
                  {projectName} pins this matrix at{" "}
                  {heldOnThisAsset?.version ?? "—"}. Removing the pin leaves the
                  project with no alert matrix of its own.
                </p>
              )}
              {/* Impact describes a write this dialog is still willing to
                  make. Once the pin has moved there is no write to describe,
                  and saying what would be "written back unchanged" would
                  describe one. */}
              {pinMoved ? null : (
                <p className="text-muted-foreground">
                  {otherPins.length === 0
                    ? "No other adoption in this project changes."
                    : `${otherPins.length} other ${otherPins.length === 1 ? "adoption" : "adoptions"} in this project ${otherPins.length === 1 ? "is" : "are"} written back unchanged.`}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Pin set {current.data.adoptionsVersion} · a pin names an
                immutable version, so a later publish never moves it.
              </p>
            </>
          )}

          {failure ? (
            <Notice role="alert" tone="destructive">
              {failure}
            </Notice>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
